import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { action, computed, get } from "@ember/object"
import { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import type { CardStack, CardStackSummary } from "@shared/models/card-stack"
import type { Course, CourseCalendarDateDefault } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import ObjectId from "@shared/util/object-id-gen"
import config from "cc-frontend/config/environment"
import findMany from "cc-frontend/helpers/find-many"
import hasManyCollectively from "cc-frontend/helpers/has-many-collectively"
import {
  insertNewTemplate,
  prepareSaveAsLessonTemplate,
} from "cc-frontend/lib/actions/course/calendar/COURSE_DATE_SAVE_AS_TEMPLATE"
import COURSE_ROTATION_SET_TEMPLATE_ID from "cc-frontend/lib/actions/course/calendar/COURSE_ROTATION_SET_TEMPLATE_ID"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import { generateBlankTemplateAttributes } from "cc-frontend/utils/card-stack/blank"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { easeIn, easeOut } from "ember-animated/easings/cosine"
import move from "ember-animated/motions/move"
import { fadeIn, fadeOut } from "ember-animated/motions/opacity"
import { compact, concat, flatMap, includes, map, uniq } from "lodash-es"
import styles from "./edit-main.module.css"

const forwardTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: -window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}
const backwardsTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: -window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}

@tagName("")
export default class EditClassMain extends Component {
  readonly styles = styles

  // args
  course!: Course
  planbook!: Planbook
  rotationCalendar!: RotationCalendar

  /** The screen we're on */
  @service dispatcher
  @service("ember-store") store

  blankTemplateId = config.BLANK_TEMPLATE_ID
  screen: string = "NAME_AND_MORE"
  // screen: string = "TEMPLATE_LIBRARY"
  // screen: string = "WEEKLY_TEMPLATES"
  selectedTemplateId = null
  setSmallWidth!: Function
  setNormalWidth!: Function
  selectedCourseDate: CourseCalendarDateDefault | null = null

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal < newVal) {
      // return toLeft
      // return toUp
      return forwardTransition
    } else {
      // return toRight
      // return toDown
      return backwardsTransition
    }
  }

  @hasManyCollectively("planbook", "course", "courses") courses
  @findMany("templateIds", "card-stack-summary") usedTemplates!: PromiseObject<
    Array<CardStackSummary>
  >

  @computed("planbook", "courses")
  get templateIds() {
    let planbookTemplateIds = map(
      get(this.planbook.relationships.cardStackTemplates, "data"),
      (datum) => get(datum, "id")
    )
    let courseTemplateIds = uniq(
      compact(flatMap(this.courses.content, (course) => extractCardStackTemplateIds(course)))
    )
    return compact(uniq(concat(planbookTemplateIds, courseTemplateIds)))
  }

  @action
  onNext() {
    this.set("screen", "ORDER")
  }

  @action
  goToOrder() {
    this.set("screen", "ORDER")
  }

  @action
  goToName() {
    this.set("screen", "NAME_AND_MORE")
  }

  @action
  onBack() {
    this.set("screen", "NAME_AND_MORE")
  }

  @action
  goToTemplateChooser() {
    this.set("screen", "TEMPLATE_LIBRARY_FIRST_TIME")
  }

  @action
  selectTemplate(id) {
    this.set("selectedTemplateId", id)
    this.set("screen", "SELECT_DAYS")
    this.setSmallWidth()
  }

  @action
  goToEditTemplateScreen() {
    this.set("screen", "EDIT_TEMPLATE")
  }

  @action
  goToWeeklyTemplates() {
    this.set("screen", "WEEKLY_TEMPLATES")
    this.setNormalWidth()
  }

  @action
  editTemplate(courseDate: CourseCalendarDateDefault, cardStack: CardStack) {
    // We need to see if we need to modify this one before we edit it.
    let cardStackTemplateIds = map(
      this.planbook.relationships.cardStackTemplates.data,
      (data) => data.id
    )
    let templateIdToEdit = cardStack.id
    let isAlreadyInPlanbook = includes(cardStackTemplateIds, cardStack.id)
    if (isAlreadyInPlanbook !== true) {
      // we need to create a copy of the template and save it and then set it.
      let newCardStackAttributes = cloneCardStackAttributesWithNewIds(
        cardStack.attributes,
        cardStack.attributes.parentCardStack,
        !!this.planbook.attributes.flags.unifiedLessons,
        {
          overrideTitle: "My " + cardStack.attributes.title,
          onMigrationError: (error) => Sentry.captureException(error),
        }
      )
      templateIdToEdit = ObjectId.create()
      // First set the new template for all days that have the existing card stack id
      this._setTemplateId(courseDate, templateIdToEdit, cardStack.id)
      // then save it to the planbook
      insertNewTemplate(this.store, templateIdToEdit, this.planbook.id, newCardStackAttributes)
      this.dispatcher.dispatchPreparedAction(
        prepareSaveAsLessonTemplate({
          newId: templateIdToEdit,
          planbookId: this.planbook.id,
          cardStackAttributes: newCardStackAttributes,
        })
      )
    }
    this.set("selectedCourseDate", courseDate)
    this.set("selectedTemplateId", templateIdToEdit)
    this.set("screen", "EDIT_TEMPLATE")
    this.setSmallWidth()
  }

  @action
  changeTemplate(courseDate) {
    this.set("selectedCourseDate", courseDate)
    this.set("screen", "TEMPLATE_LIBRARY")
  }

  @action
  chooseTemplate(template) {
    if (this.selectedCourseDate === null) throw Error("There wasn't a courseDate set")
    this._setTemplateId(this.selectedCourseDate, template.id)
    this.set("screen", "WEEKLY_TEMPLATES")
  }

  @action
  removeTemplate(courseDate) {
    this._setTemplateId(courseDate, this.blankTemplateId)
  }

  @action
  createAndAssignTemplateRotationDay(cardStackAttributes, courseDate) {
    // if (this.selectedCourseDate === null)
    //   return Error("Cannot create template if no selected course date")
    // Make a new blank template
    let newBlankTemplateAttributes = generateBlankTemplateAttributes(this.planbook)
    let id = ObjectId.create()
    // Save new blank template to planbook
    insertNewTemplate(this.store, id, this.planbook.id, newBlankTemplateAttributes)
    this.dispatcher.dispatchPreparedAction(
      prepareSaveAsLessonTemplate({
        newId: id,
        planbookId: this.planbook.id,
        cardStackAttributes: newBlankTemplateAttributes,
      })
    )
    // Set selected template id to new template id so the right template to edit comes up
    this.set("selectedTemplateId", id)
    this.setSmallWidth()
    this.goToEditTemplateScreen()
    // Apply to every selected courseDate.rotationDay going forward
    this._setTemplateId(courseDate, id)
  }

  @action
  assignBlankTemplateToRotationDay(courseDate, templateId) {
    this._setTemplateId(courseDate, templateId)
  }

  _setTemplateId(
    courseDate: CourseCalendarDateDefault,
    cardStackId: string | null,
    filterForExistingTemplateId: string | undefined = undefined
  ): void {
    this.dispatcher.dispatchAction(COURSE_ROTATION_SET_TEMPLATE_ID, {
      courseId: this.course.id,
      rotationId: courseDate.attributes.rotationId,
      semesterId: courseDate.attributes.semesterId,
      planbookId: this.planbook.id,
      dateId: courseDate.id,
      cardStackId: cardStackId,
      currentTemplateId: courseDate.attributes.cardStackId,
    })
  }
}
